import { useSupabaseTypedClient } from 'hooks/reactQuery';
import { extractCodeFromStorage, useCodeStorage, STORAGE_QUOTA_EVENT } from 'hooks/useCodeStorage';
import { useEditorSettings } from 'hooks/useEditorSettings';
import { useToastWrapper } from 'hooks/useToastWrapper';
import { debounce, isEqual } from 'lodash';
import React, {
  ReactNode,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';

import { EdgeFunctionName } from 'types/edgeFunctions';

import { stripComments, stripCommentsAndStrings } from './codeStripping';
import { processCSS } from './cssProcessors';
import { getWorkerCode } from './testWorker';
import {
  IDevSandboxProps,
  ICodePreviewContextData,
  IDemoSpellState,
  TEditorFramework,
  TCSSMode,
  IEditorPackage,
  getTabLabels,
  clampSettingsForMode,
} from './types';
import useResolvedTheme from './useResolvedTheme';

type CodePreviewContextData = ICodePreviewContextData;

// Dynamically load Babel standalone and transpile TypeScript on the main thread
let babelPromise: Promise<any> | null = null;
const loadBabel = (): Promise<any> => {
  if ((window as any).Babel) return Promise.resolve((window as any).Babel);
  if (babelPromise) return babelPromise;
  babelPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = 'https://unpkg.com/@babel/standalone/babel.min.js';
    script.onload = () => resolve((window as any).Babel);
    script.onerror = () => reject(new Error('Failed to load Babel'));
    document.head.appendChild(script);
  });
  return babelPromise;
};

const transpileTypeScript = async (code: string): Promise<string> => {
  const Babel = await loadBabel();
  return Babel.transform(code, {
    filename: 'file.ts',
    presets: [['typescript', { allExtensions: true }]],
  }).code;
};

export const CodePreviewContext = React.createContext({} as CodePreviewContextData);

interface IDevSandboxProviderProps extends IDevSandboxProps {
  children: ReactNode;
}

interface IGeneratePageOptions {
  framework?: TEditorFramework;
  cssMode?: TCSSMode;
  packages?: IEditorPackage[];
  classMap?: Record<string, string>;
}

export const getGeneratedPageURL = (
  html = '',
  css = '',
  js = '',
  options?: IGeneratePageOptions
) => {
  const { framework = 'vanilla', packages = [], classMap } = options || {};

  const getBlobURL = (code: string, type: string) => {
    const blob = new Blob([code], { type });
    return URL.createObjectURL(blob);
  };

  const cssURL = getBlobURL(css, 'text/css');

  // Strip comments and strings before detection to avoid false positives
  // (e.g. "// React is cool" or 'const msg = "useState"' no longer trigger CDN injection)
  const cleanJs = stripCommentsAndStrings(js);

  // Auto-detection flags (used as fallback when framework is 'vanilla')
  const hasJsxKeywords = cleanJs.includes('React') || cleanJs.includes('jsx') || cleanJs.includes('useState') || cleanJs.includes('useEffect') || cleanJs.includes('createRoot') || cleanJs.includes('</');
  const hasVueKeywords = cleanJs.includes('Vue.') || cleanJs.includes('createApp') || cleanJs.includes('.mount(');
  const hasTypeScriptKeywords = cleanJs.includes(': string') || cleanJs.includes(': number') || cleanJs.includes(': boolean') || cleanJs.includes('interface ') || cleanJs.includes('<T>') || cleanJs.includes(': void');
  const hasSvelte = cleanJs.includes('importSvelteBundle') || cleanJs.includes('svelte-browser-import');
  const hasAngular = cleanJs.includes('@angular/core') || cleanJs.includes('bootstrapApplication');
  const hasEsModule = cleanJs.includes('import ') && cleanJs.includes(' from ');

  // Determine effective framework: explicit setting or auto-detect
  let effectiveFramework: TEditorFramework | 'angular' | 'svelte' | 'esmodule' = framework;
  if (framework === 'vanilla') {
    // Auto-detection fallback for backward compatibility
    if (hasAngular) effectiveFramework = 'angular';
    else if (hasJsxKeywords) effectiveFramework = 'react';
    else if (hasVueKeywords) effectiveFramework = 'vue';
    else if (hasTypeScriptKeywords) effectiveFramework = 'typescript';
    else if (hasSvelte) effectiveFramework = 'svelte';
    else if (hasEsModule) effectiveFramework = 'esmodule';
  }

  let headTag = '';
  let jsTag = '';

  // Generate import map for packages if any
  if (packages.length > 0) {
    const imports = packages.reduce((acc, pkg) => {
      acc[pkg.name] = `https://esm.sh/${pkg.name}@${pkg.version}`;
      return acc;
    }, {} as Record<string, string>);

    headTag += `<script type="importmap">
      ${JSON.stringify({ imports }, null, 2)}
    <\/script>`;
  }

  if (js) {
    if (effectiveFramework === 'angular' || hasAngular) {
      // Angular needs its own import map
      const angularImportMap = packages.length > 0 ? '' : `<script type="importmap">
        {
          "imports": {
            "@angular/core": "https://cdn.jsdelivr.net/npm/@esm-bundle/angular@19.2.0/es2022/angular-core.js",
            "@angular/common": "https://cdn.jsdelivr.net/npm/@esm-bundle/angular@19.2.0/es2022/angular-common.js",
            "@angular/compiler": "https://cdn.jsdelivr.net/npm/@esm-bundle/angular@19.2.0/es2022/angular-compiler.js",
            "@angular/platform-browser": "https://cdn.jsdelivr.net/npm/@esm-bundle/angular@19.2.0/es2022/angular-platform-browser.js",
            "@angular/platform-browser-dynamic": "https://cdn.jsdelivr.net/npm/@esm-bundle/angular@19.2.0/es2022/angular-platform-browser-dynamic.js",
            "rxjs": "https://cdn.jsdelivr.net/npm/rxjs@7.8.1/+esm",
            "rxjs/operators": "https://cdn.jsdelivr.net/npm/rxjs@7.8.1/operators/+esm",
            "tslib": "https://cdn.jsdelivr.net/npm/tslib@2.6.2/tslib.es6.mjs"
          }
        }
        <\/script>`;
      headTag = angularImportMap + `<script src="https://cdn.jsdelivr.net/npm/zone.js@0.14.3/dist/zone.min.js"><\/script>`;
      jsTag = `<script type="module">${js}<\/script>`;
    } else if (effectiveFramework === 'react') {
      jsTag = `<script crossorigin="anonymous" src="https://unpkg.com/react@18/umd/react.development.js"><\/script>
         <script crossorigin="anonymous" src="https://unpkg.com/react-dom@18/umd/react-dom.development.js"><\/script>
         <script crossorigin="anonymous" src="https://unpkg.com/@babel/standalone/babel.min.js"><\/script>
         <script type="text/babel" data-presets="react">${js}<\/script>`;
    } else if (effectiveFramework === 'vue') {
      jsTag = `<script crossorigin="anonymous" src="https://unpkg.com/vue@3/dist/vue.global.js"><\/script>
         <script>${js}<\/script>`;
    } else if (effectiveFramework === 'typescript') {
      if (framework === 'typescript') {
        // Explicitly set to TS — code is pre-transpiled on main thread, inject as plain JS
        jsTag = `<script>${js}<\/script>`;
      } else {
        // Auto-detected TS from vanilla — use Babel in iframe
        // Must use Babel.availablePresets['typescript'] (object reference), NOT the string 'typescript'.
        // String references trigger module path resolution which fails in browser/Blob URL context
        // with "Cannot load preset typescript relative to /"
        jsTag = `<script crossorigin="anonymous" src="https://unpkg.com/@babel/standalone/babel.min.js"><\/script>
           <script>Babel.registerPreset('ts', { presets: [[Babel.availablePresets['typescript'], { allExtensions: true }]] });<\/script>
           <script type="text/babel" data-presets="ts">${js}<\/script>`;
      }
    } else if (effectiveFramework === 'svelte' || hasSvelte) {
      jsTag = `<script type="module">${js}<\/script>`;
    } else if (effectiveFramework === 'esmodule' || hasEsModule) {
      jsTag = `<script type="module">${js}<\/script>`;
    } else {
      jsTag = `<script>${js}<\/script>`;
    }
  }

  // Error overlay script — shows runtime errors as semi-transparent banner in iframe
  const errorOverlayScript = `<script>
    function showErrorOverlay(message, errorType) {
      // Extract just the error type and message, skip stack traces
      var cleanMessage = message;
      if (message.indexOf('\\n') !== -1) {
        cleanMessage = message.split('\\n')[0];
      }
      // Remove blob URL references
      cleanMessage = cleanMessage.replace(/at blob:[^\\s]+/g, '').replace(/\\s+$/, '');
      // Extract error type from message if present
      var typeMatch = cleanMessage.match(/^(TypeError|ReferenceError|SyntaxError|Error):/i);
      var displayType = errorType || (typeMatch ? typeMatch[1] : 'Error');
      cleanMessage = cleanMessage.replace(/^(Error:|TypeError:|ReferenceError:|SyntaxError:)\\s*/i, '');

      var existing = document.getElementById('bds-error-overlay');
      if (existing) existing.remove();

      var overlay = document.createElement('div');
      overlay.id = 'bds-error-overlay';
      overlay.style.cssText = [
        'position:fixed',
        'top:0',
        'left:0',
        'right:0',
        'padding:14px 48px 14px 16px',
        'background:rgba(15,10,10,0.94)',
        'backdrop-filter:blur(12px)',
        '-webkit-backdrop-filter:blur(12px)',
        'border-bottom:2px solid rgba(248,113,113,0.5)',
        'box-shadow:0 4px 24px rgba(0,0,0,0.5)',
        'z-index:2147483647',
        'font-family:-apple-system,BlinkMacSystemFont,Segoe UI,Roboto,sans-serif',
        'display:flex',
        'align-items:center',
        'gap:12px',
        'isolation:isolate'
      ].join(';');

      // JS tab badge
      var badge = document.createElement('span');
      badge.textContent = 'JS';
      badge.style.cssText = [
        'background:rgba(248,113,113,0.15)',
        'color:#f87171',
        'padding:4px 10px',
        'border-radius:4px',
        'font-size:11px',
        'font-weight:700',
        'letter-spacing:0.5px',
        'flex-shrink:0'
      ].join(';');

      // Error type label
      var typeLabel = document.createElement('span');
      typeLabel.textContent = displayType + ':';
      typeLabel.style.cssText = [
        'color:#f87171',
        'font-weight:600',
        'font-size:14px',
        'flex-shrink:0'
      ].join(';');

      // Error message
      var msgSpan = document.createElement('span');
      msgSpan.textContent = cleanMessage;
      msgSpan.style.cssText = [
        'color:#fecaca',
        'font-size:14px',
        'line-height:1.4',
        'font-family:ui-monospace,SFMono-Regular,Menlo,Monaco,monospace',
        'overflow:hidden',
        'text-overflow:ellipsis',
        'white-space:nowrap'
      ].join(';');

      // Close button
      var closeBtn = document.createElement('button');
      closeBtn.innerHTML = '&times;';
      closeBtn.style.cssText = [
        'position:absolute',
        'top:50%',
        'right:12px',
        'transform:translateY(-50%)',
        'background:rgba(255,255,255,0.08)',
        'border:1px solid rgba(255,255,255,0.1)',
        'color:#fca5a5',
        'cursor:pointer',
        'font-size:20px',
        'line-height:1',
        'width:28px',
        'height:28px',
        'border-radius:6px',
        'display:flex',
        'align-items:center',
        'justify-content:center',
        'transition:all 0.15s'
      ].join(';');
      closeBtn.onmouseover = function() {
        this.style.background = 'rgba(255,255,255,0.15)';
        this.style.borderColor = 'rgba(255,255,255,0.2)';
      };
      closeBtn.onmouseout = function() {
        this.style.background = 'rgba(255,255,255,0.08)';
        this.style.borderColor = 'rgba(255,255,255,0.1)';
      };
      closeBtn.onclick = function() { overlay.remove(); };

      overlay.appendChild(badge);
      overlay.appendChild(typeLabel);
      overlay.appendChild(msgSpan);
      overlay.appendChild(closeBtn);
      document.body.appendChild(overlay);
    }
    window.onerror = function(msg, url, line, col, error) {
      // Skip CORS-sanitized "Script error." — provides no useful info
      if (typeof msg === 'string' && msg === 'Script error.' && !error) return true;
      var message = (error && error.message) ? error.message : (typeof msg === 'string' ? msg : 'Unknown error');
      var errorType = error && error.name ? error.name : null;
      showErrorOverlay(message, errorType);
      return true;
    };
    window.addEventListener('error', function(e) {
      if (e.error) {
        var errMsg = e.error.message || (typeof e.error === 'string' ? e.error : 'Unknown error');
        showErrorOverlay(errMsg, e.error.name);
      } else if (e.message) {
        showErrorOverlay(e.message);
      }
    });
    window.onunhandledrejection = function(e) {
      var reason = e.reason;
      var message;
      if (!reason) {
        message = 'Unhandled Promise rejection';
      } else if (typeof reason === 'string') {
        message = reason;
      } else if (reason instanceof Error) {
        message = reason.message;
      } else {
        try { message = JSON.stringify(reason); } catch(err) { message = 'Unhandled Promise rejection'; }
      }
      var errorType = reason && reason.name ? reason.name : 'Promise';
      showErrorOverlay(message, errorType);
    };
  <\/script>`;

  // Console override script — captures console output and sends to parent
  // Also listens for eval requests from parent (console input)
  const consoleOverrideScript = `<script>
    (function() {
      var origConsole = { log: console.log, warn: console.warn, error: console.error, info: console.info };
      ['log', 'warn', 'error', 'info'].forEach(function(method) {
        console[method] = function() {
          origConsole[method].apply(console, arguments);
          var args = Array.prototype.slice.call(arguments).map(function(a) {
            try { return typeof a === 'object' ? JSON.stringify(a, null, 2) : String(a); }
            catch(e) { return String(a); }
          });
          parent.postMessage({ type: 'bds-console', method: method, args: args }, '*');
        };
      });

      // Listen for eval requests from parent console panel
      window.addEventListener('message', function(event) {
        if (event.data && event.data.type === 'bds-eval') {
          try {
            var result = eval(event.data.code);
            if (result !== undefined) {
              console.log(result);
            }
          } catch (err) {
            console.error(err.message || String(err));
          }
        }
      });
    })();
  <\/script>`;

  // bds.api() bridge — lets users call BDS mock APIs from their code
  const bdsApiBridgeScript = `<script>
    (function() {
      window.bds = {
        api: function(path, options) {
          return new Promise(function(resolve, reject) {
            var requestId = Math.random().toString(36).substr(2, 9);
            function handler(event) {
              if (event.data && event.data.type === 'bds-api-response' && event.data.requestId === requestId) {
                window.removeEventListener('message', handler);
                if (event.data.error) {
                  reject(new Error(event.data.error));
                } else {
                  resolve({
                    ok: event.data.status >= 200 && event.data.status < 300,
                    status: event.data.status,
                    json: function() { return Promise.resolve(event.data.body); },
                    text: function() { return Promise.resolve(typeof event.data.body === 'string' ? event.data.body : JSON.stringify(event.data.body)); },
                    headers: event.data.headers || {}
                  });
                }
              }
            }
            window.addEventListener('message', handler);
            parent.postMessage({
              type: 'bds-api-request',
              requestId: requestId,
              path: path,
              method: (options && options.method) || 'GET',
              headers: options && options.headers,
              body: options && options.body
            }, '*');
          });
        }
      };
    })();
  <\/script>`;

  const source = `
    <html>
      <head>
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      ${headTag}
      </head>
      <body style="margin: 0">
      ${errorOverlayScript}
      ${consoleOverrideScript}
      ${bdsApiBridgeScript}
      ${classMap ? `<script>window.styles=${JSON.stringify(classMap)};<\/script>` : ''}
      ${html || ''}
      ${css && `<link rel="stylesheet" type="text/css" href="${cssURL}" />`}
      ${jsTag}
      </body>
    </html>
  `;

  return getBlobURL(source, 'text/html');
};

const DevSandboxProvider = forwardRef(
  (
    {
      id,
      oldKey,
      title,
      externalCodeUrl,
      browserUrl,
      mode,
      navigation,
      codeTabs,
      paneTabs,
      iframe,
      footer,
      flags,
      testCases,
      dimensions,
      children,
      callbacks,
      entityType,
      entityId,
      merlinConfig,
      authorEditorSettings,
      initialLayout,
    }: IDevSandboxProviderProps,
    ref
  ) => {
    const { pathname } = useLocation();
    const supabase = useSupabaseTypedClient();

    const { toastError } = useToastWrapper();

    // Merlin AI overlay panel state — auto-open when autoTrigger or autoOpen is configured
    const [isMerlinOpen, setIsMerlinOpen] = useState(Boolean(merlinConfig?.autoTrigger || merlinConfig?.autoOpen));

    // Demo spell state — persists across MerlinPanel mount/unmount cycles
    // When autoOpen is set, start in loading state so the overlay covers the preview from frame 1
    // (prevents flash of template code before the spell starts)
    const shouldAutoSpell = Boolean(merlinConfig?.autoOpen && merlinConfig?.isDemo);
    const [demoSpellState, setDemoSpellState] = useState<IDemoSpellState>(() => ({
      isSpellActive: false,
      isSpellLoading: shouldAutoSpell,
      savedCode: shouldAutoSpell ? (merlinConfig?.originalCode || null) : null,
      // Random initial index so getRandomSpellIndex avoids a different spell each page load
      spellIndex: Math.floor(Math.random() * 1000),
    }));

    // Shared cancellation refs for demo spell streaming — used by both MerlinPanel (to drive streaming)
    // and handleMerlinClose/handleMerlinToggle (to cancel in-flight spells before reverting code)
    const demoSpellCancelRef = useRef(false);
    const demoSpellTimeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);

    // Editor settings (persisted per-user, with optional author override for solution viewing)
    const { settings: rawEditorSettings, updateSettings: updateEditorSettings, resetSettings: resetEditorSettings } = useEditorSettings({
      authorOverride: authorEditorSettings,
    });

    // Clamp settings to valid values for the current mode (e.g. practice problems: JS/TS + horizontal only)
    const editorSettings = clampSettingsForMode(rawEditorSettings, mode);

    // Resolve theme: 'system' → actual 'dark'|'light' based on OS preference
    const resolvedTheme = useResolvedTheme(editorSettings.theme);

    // Compute dynamic tab names based on settings
    const dynamicTabNames = getTabLabels(editorSettings.framework, editorSettings.cssMode);

    const baseStorageKey = id;

    // For demo auto-spell, clear stale spell code from localStorage ONCE on first render.
    // Without this, useCodeStorage initializes with old spell code from a previous session,
    // causing visible flashing (old spell → template → new spell).
    // MUST only run once — running on every render would wipe spell code written by onHtmlChange,
    // because useLocalStorageState (useSyncExternalStore) reads localStorage on every render.
    const localStorageClearedRef = useRef(false);
    if (shouldAutoSpell && !localStorageClearedRef.current) {
      localStorageClearedRef.current = true;
      localStorage.removeItem(`${baseStorageKey}-html`);
      localStorage.removeItem(`${baseStorageKey}-css`);
      localStorage.removeItem(`${baseStorageKey}-js`);
    }

    const [html, setHtml] = useCodeStorage({
      key: `${baseStorageKey}-html`,
      oldKey: oldKey ? `${oldKey}-html` : undefined,
      defaultValue: codeTabs?.html || '',
    });
    const [css, setCss] = useCodeStorage({
      key: `${baseStorageKey}-css`,
      oldKey: oldKey ? `${oldKey}-css` : undefined,
      defaultValue: codeTabs?.css || '',
    });
    const [js, setJs] = useCodeStorage({
      key: `${baseStorageKey}-js`,
      oldKey: oldKey ? `${oldKey}-js` : undefined,
      defaultValue: codeTabs?.js || '',
    });

    // Cancel any in-flight demo spell streaming — stops all pending timeouts so they can't
    // overwrite code AFTER we revert. Must run BEFORE setHtml/setCss/setJs.
    const cancelDemoSpell = useCallback(() => {
      demoSpellCancelRef.current = true;
      demoSpellTimeoutsRef.current.forEach(clearTimeout);
      demoSpellTimeoutsRef.current = [];
    }, []);

    // Keep a ref to always have the latest originalCode without stale closures
    const originalCodeRef = useRef(merlinConfig?.originalCode);
    originalCodeRef.current = merlinConfig?.originalCode;

    // Revert demo code to original template — cancels streaming, restores code, resets state.
    // Uses ref to avoid stale closures in useCallback.
    const revertDemoSpell = useCallback(() => {
      cancelDemoSpell();
      const restoreCode = originalCodeRef.current;
      if (restoreCode) {
        setHtml(restoreCode.html);
        setCss(restoreCode.css);
        setJs(restoreCode.js);
      }
      setDemoSpellState((prev) => ({ ...prev, isSpellActive: false, isSpellLoading: false }));
    }, [cancelDemoSpell, setHtml, setCss, setJs]);

    const isDemo = Boolean(merlinConfig?.isDemo);

    // Merlin handlers — must be after useCodeStorage so setHtml/setCss/setJs are available
    const handleMerlinToggle = useCallback(() => {
      setIsMerlinOpen((prev) => !prev);
    }, []);

    const handleMerlinOpen = useCallback(() => {
      setIsMerlinOpen(true);
    }, []);

    const handleMerlinClose = useCallback(() => {
      setIsMerlinOpen(false);
    }, []);

    // Cmd/Ctrl+Shift+M keyboard shortcut to toggle Merlin AI panel
    // (Cmd+M alone minimizes the window on macOS)
    // Disabled when viewing solutions (hideTools = true)
    const isReadOnlyView = footer?.hideTools || false;
    useEffect(() => {
      if (isReadOnlyView) return;
      const handler = (e: KeyboardEvent) => {
        const isMod = e.metaKey || e.ctrlKey;
        if (isMod && e.shiftKey && e.key.toLowerCase() === 'm') {
          e.preventDefault();
          e.stopPropagation();
          handleMerlinToggle();
        }
      };
      window.addEventListener('keydown', handler);
      return () => window.removeEventListener('keydown', handler);
    }, [isReadOnlyView, handleMerlinToggle]);

    // practice problems state management
    const [hasRun, setHasRun] = useState(false);
    const [outputs, setOutputs] = useState<unknown[]>([]);
    const [testError, setTestError] = useState<string | null>(null);

    const passedTestsCount =
      testCases?.tests.filter((test, index) => {
        return isEqual(test.output, outputs[index]);
      }).length || 0;

    const [codeEditorWidth, setCodeEditorWidth] = useState(dimensions?.codeEditorWidth || 0);
    const [previewWidth, setPreviewWidth] = useState(dimensions?.previewWidth || 0);

    const [codeFrameUrl, setCodeFrameUrl] = useState(iframe?.codeFrameUrl || '');
    const [bufferFrameUrl, setBufferFrameUrl] = useState(iframe?.bufferFrameUrl || '');

    const [codeTabIndex, setCodeTabIndex] = useState(codeTabs?.tabIndex || 0);
    const [paneTabIndex, setPaneTabIndex] = useState(paneTabs?.tabIndex || 0);

    useEffect(() => {
      setPaneTabIndex(0);
    }, [pathname]);

    useEffect(() => {
      // Skip code loading when auto-spell is about to fire — the spell effect will
      // handle code. Without this, this effect sets CUSTOM_DEFAULT which briefly
      // flashes the template in the iframe before the spell overwrites it.
      if (shouldAutoSpell) return;

      if (baseStorageKey === 'new') {
        setHtml('');
        setCss('');
        setJs('');

        return;
      }

      const htmlFromStorage = baseStorageKey
        ? extractCodeFromStorage({ key: baseStorageKey, code: 'html' })
        : '';
      const cssFromStorage = baseStorageKey
        ? extractCodeFromStorage({ key: baseStorageKey, code: 'css' })
        : '';
      const jsFromStorage = baseStorageKey
        ? extractCodeFromStorage({ key: baseStorageKey, code: 'js' })
        : '';

      // Prioritize loading code from server, e.g. when browsing user solution
      if (flags?.loadCodeFromServer) {
        setHtml(codeTabs?.html || '');
        setCss(codeTabs?.css || '');
        setJs(codeTabs?.js || '');

        return;
      }

      setHtml(htmlFromStorage || codeTabs?.html || '');
      setCss(cssFromStorage || codeTabs?.css || '');
      setJs(jsFromStorage || codeTabs?.js || '');
    }, [baseStorageKey]);

    // BUG-02: Listen for localStorage quota exceeded events and warn user
    useEffect(() => {
      const handler = () => {
        toastError(
          'Storage is full — your latest changes may not be saved. Consider clearing old projects.',
          true
        );
      };
      window.addEventListener(STORAGE_QUOTA_EVENT, handler);
      return () => window.removeEventListener(STORAGE_QUOTA_EVENT, handler);
    }, []);

    // bds.api() parent listener — handles proxy requests from iframe
    const handleBdsApiRequest = useCallback(
      async (event: MessageEvent) => {
        if (!event.data || event.data.type !== 'bds-api-request') return;

        const { requestId, path, method, headers, body } = event.data;
        const source = event.source as WindowProxy | null;
        if (!source) return;

        try {
          const { data, error } = await supabase.functions.invoke(
            EdgeFunctionName.ProxyFetch,
            {
              body: {
                path,
                method: method || 'GET',
                headers: headers || {},
                body: body || undefined,
                entityType: entityType || undefined,
                entityId: entityId || id,
              },
            }
          );

          if (error) {
            source.postMessage(
              { type: 'bds-api-response', requestId, error: String(error) },
              '*'
            );
            return;
          }

          source.postMessage(
            {
              type: 'bds-api-response',
              requestId,
              status: data?.status ?? 200,
              body: data?.body,
              headers: data?.headers || {},
            },
            '*'
          );
        } catch (err) {
          source.postMessage(
            {
              type: 'bds-api-response',
              requestId,
              error: err instanceof Error ? err.message : 'Request failed',
            },
            '*'
          );
        }
      },
      [supabase, entityType, entityId, id]
    );

    useEffect(() => {
      window.addEventListener('message', handleBdsApiRequest);
      return () => window.removeEventListener('message', handleBdsApiRequest);
    }, [handleBdsApiRequest]);

    const handleRunTestsClick = async (e: React.MouseEvent<HTMLButtonElement>): Promise<number> => {
      e.stopPropagation();

      if (!js?.includes(testCases?.function_name || '')) {
        setTestError(`${testCases?.function_name} function must be defined in the code`);
        return 0;
      }

      let sanitizedJs = stripComments(js);

      // Transpile TypeScript to JavaScript on the main thread before sending to worker
      if (editorSettings.framework === 'typescript') {
        try {
          sanitizedJs = await transpileTypeScript(sanitizedJs);
        } catch (err) {
          setTestError(`TypeScript transpilation failed: ${err}`);
          return 0;
        }
      }

      return new Promise<number>((resolve) => {
        const workerBlob = new Blob([getWorkerCode()], { type: 'application/javascript' });
        const workerUrl = URL.createObjectURL(workerBlob);
        const worker = new Worker(workerUrl);

        const cleanup = () => {
          worker.terminate();
          URL.revokeObjectURL(workerUrl);
        };

        const timeoutId = setTimeout(() => {
          cleanup();
          setTestError('Execution timed out after 5 seconds. Check for infinite loops.');
          setOutputs([]);
          setHasRun(true);
          resolve(0);
        }, 5000);

        worker.onmessage = (event) => {
          // Forward worker console output to ConsolePanel via postMessage
          if (event.data.type === 'console') {
            window.postMessage({ type: 'bds-console', method: event.data.method, args: event.data.args }, '*');
            return;
          }

          clearTimeout(timeoutId);
          cleanup();

          if (event.data.type === 'success') {
            setTestError(null);
            const workerOutputs = event.data.outputs;
            setOutputs(workerOutputs);
            setHasRun(true);
            resolve(
              testCases?.tests.filter((test, index) =>
                isEqual(test.output, workerOutputs[index])
              ).length || 0
            );
          } else {
            setTestError(`Error executing user code: ${event.data.message}`);
            setOutputs([]);
            setHasRun(true);
            resolve(0);
          }
        };

        worker.onerror = (error) => {
          clearTimeout(timeoutId);
          cleanup();
          setTestError(`Worker error: ${error.message}`);
          setOutputs([]);
          setHasRun(true);
          resolve(0);
        };

        worker.postMessage({
          code: sanitizedJs,
          functionName: testCases?.function_name,
          tests: testCases?.tests.map((t) => ({ input: t.input })),
        });
      });
    };

    useImperativeHandle(ref, () => ({
      updateHtml: setHtml,
      runTests: handleRunTestsClick,
      onCodeFrameUrlChange: updateIframeContent,
    }));

    const updateIframeContent = async () => {
      try {
        // Process CSS based on mode (SCSS, PostCSS, CSS Modules)
        const { css: processedCSS, html: processedHTML, classMap } = await processCSS(
          css,
          html,
          editorSettings.cssMode
        );

        // Pre-transpile TypeScript on the main thread so the iframe gets plain JS
        let processedJs = js;
        if (editorSettings.framework === 'typescript' && js) {
          try {
            processedJs = await transpileTypeScript(js);
          } catch {
            // Let the iframe show the original code — Babel in-iframe will show the error
          }
        }

        const newUrl = getGeneratedPageURL(processedHTML, processedCSS, processedJs, {
          framework: editorSettings.framework,
          cssMode: editorSettings.cssMode,
          packages: editorSettings.packages,
          classMap,
        });

        setCodeFrameUrl(newUrl);

        callbacks?.onCodeFrameUrlChange(newUrl);
      } catch {
        // Silently swallow — prevents unhandled rejection & CRA error overlay
      }
    };

    const debouncedUpdateIframeContent = debounce(updateIframeContent, 500);
    const fastDebouncedUpdateIframeContent = debounce(updateIframeContent, 80);
    const isMerlinStreamingRef = useRef(false);

    // Track whether the initial auto-spell has applied code yet.
    // While true, suppress iframe updates so the template Blob URL is never generated —
    // this is the definitive fix for the template code flash/blink during spell loading.
    const spellPendingRef = useRef(shouldAutoSpell);

    // Listen for Merlin streaming events to switch debounce speed
    useEffect(() => {
      const handler = (e: Event) => {
        const { isStreaming: streaming } = (e as CustomEvent).detail;
        isMerlinStreamingRef.current = streaming;
      };
      window.addEventListener('merlin-streaming', handler);
      return () => window.removeEventListener('merlin-streaming', handler);
    }, []);

    useEffect(() => {
      // While spell is pending (loading overlay visible, spell code not yet applied),
      // suppress ALL iframe updates. The first Blob URL will be the spell code, not template.
      if (spellPendingRef.current) {
        if (demoSpellState.isSpellLoading) return;
        // Spell code just applied — allow iframe updates from now on
        spellPendingRef.current = false;
      }

      if (isMerlinStreamingRef.current) {
        debouncedUpdateIframeContent.cancel();
        fastDebouncedUpdateIframeContent();
      } else {
        fastDebouncedUpdateIframeContent.cancel();
        debouncedUpdateIframeContent();
      }

      return () => {
        debouncedUpdateIframeContent.cancel();
        fastDebouncedUpdateIframeContent.cancel();
      };
    }, [html, css, js, editorSettings.framework, editorSettings.cssMode, editorSettings.packages, demoSpellState.isSpellLoading]);

    return (
      <CodePreviewContext.Provider
        value={{
          id,
          title,
          browserUrl,
          externalCodeUrl: flags?.isExternalCode ? externalCodeUrl : '',
          oldKey: oldKey || undefined,
          mode: mode || 'interactive',
          navigation: {
            leftSide: navigation?.leftSide || null,
            header: navigation?.header || null,
            rightSide: navigation?.rightSide || null,
          },
          codeTabs: {
            ...codeTabs,
            html,
            css,
            js,
            tabIndex: codeTabIndex,
            // Use dynamic tab names based on editor settings, fallback to provided or default
            // For script-practice mode, use only the JS label (last element of dynamicTabNames)
            tabNames: mode === 'script-practice'
              ? [dynamicTabNames[2]]
              : (codeTabs?.tabNames || dynamicTabNames),
          },
          paneTabs: {
            ...paneTabs,
            tabIndex: paneTabIndex,
            tabs: paneTabs?.tabs || [],
          },
          testCases: testCases || { input_names: [], tests: [], function_name: '' },
          iframe: {
            codeFrameUrl,
            bufferFrameUrl,
          },
          footer: {
            leftSide: footer?.leftSide || null,
            header: footer?.header || null,
            rightSide: footer?.rightSide || null,
            assetsConfig: footer?.assetsConfig ? {
              ...footer.assetsConfig,
              editorPackages: footer.assetsConfig.editorPackages ?? editorSettings.packages,
              onPackagesChange: footer.assetsConfig.onPackagesChange ?? ((packages) => updateEditorSettings({ packages })),
            } : null,
            hideTools: footer?.hideTools || false,
            showForkHint: footer?.showForkHint,
          },
          flags: {
            loadCodeFromServer: flags?.loadCodeFromServer || false,
            isExternalCode: flags?.isExternalCode || false,
          },
          dimensions: {
            codeEditorWidth,
            previewWidth,
          },
          handlers: {
            onHtmlChange: setHtml,
            onCssChange: setCss,
            onJsChange: setJs,
            onCodeTabIndexChange: setCodeTabIndex,
            onPaneTabIndexChange: setPaneTabIndex,
            onCodeFrameUrlChange: updateIframeContent,
            onCodeEditorWidthChange: setCodeEditorWidth,
            onPreviewWidthChange: setPreviewWidth,
            onCodeFrameChange: setCodeFrameUrl,
            onBufferFrameChange: setBufferFrameUrl,
            onRunTestsClick: handleRunTestsClick,
            onSetOutputs: setOutputs,
            onEditorSettingsChange: updateEditorSettings,
            onEditorSettingsReset: resetEditorSettings,
          },
          callbacks: callbacks || {
            onCodeFrameUrlChange: (codeFrameUrl: string) => setCodeFrameUrl(codeFrameUrl),
          },
          state: {
            practiceProblemChecks: {
              hasRun,
              outputs,
              passedTestsCount,
              testError,
            },
          },
          entityType,
          editorSettings,
          resolvedTheme,
          initialLayout,
          merlin: {
            isOpen: isMerlinOpen,
            config: merlinConfig || null,
            onOpen: handleMerlinOpen,
            onToggle: handleMerlinToggle,
            onClose: handleMerlinClose,
          },
          demoSpell: {
            state: demoSpellState,
            setState: setDemoSpellState,
            cancelRef: demoSpellCancelRef,
            timeoutsRef: demoSpellTimeoutsRef,
            revert: revertDemoSpell,
          },
        }}
      >
        {children}
      </CodePreviewContext.Provider>
    );
  }
);

export const useDevSandbox = (): CodePreviewContextData => {
  const context = useContext(CodePreviewContext);

  if (!context) {
    throw new Error('useDevSandbox must be used within an DevSandboxContentProvider');
  }

  return context;
};

export default DevSandboxProvider;
